import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Event, EVENT_TYPES, STATUS_CONFIG, EventType, EventStatus } from '../types';
import { Edit, Trash2, MapPin, FileText } from 'lucide-react';

const ListContainer = styled.div`
  background: #202020;
  border-radius: 0.75rem;
  border: 1px solid #27272f;
  overflow: hidden;
`;

const ListHeader = styled.div`
  background: #202020;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #383836;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => (props.active ? '#52525b' : '#383836')};
  border-radius: 0.5rem;
  background: ${props => (props.active ? '#383836' : 'transparent')};
  color: ${props => (props.active ? '#e5e7eb' : '#9ca3af')};
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background: ${props => (props.active ? '#383836' : '#2d2d2d')};
    color: #e5e7eb;
  }
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #383836;
  border-radius: 0.5rem;
  background: #2d2d2d;
  color: #e5e7eb;
  font-weight: 500;
`;

const EventList = styled.div`
  max-height: 500px;
  overflow-y: auto;
  background: #202020;
`;

const EventItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #383836;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background: #2d2d2d;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const EventIcon = styled.div`
  font-size: 1.5rem;
  width: 2rem;
  text-align: center;
`;

const EventContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const EventName = styled.div`
  font-weight: 600;
  color: #e5e7eb;
  font-size: 1rem;
`;

const EventDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StatusBadge = styled.div<{ status: EventStatus }>`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  background: ${props => STATUS_CONFIG[props.status].color};
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const EventActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #383836;
    color: #e5e7eb;
  }

  &.delete:hover {
    background: rgba(220, 38, 38, 0.2);
    color: #f87171;
  }
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  color: #9ca3af;
`;

interface ListViewProps {
  events: Event[];
}

type SortOption = 'date' | 'type' | 'status' | 'name';
type FilterOption = 'all' | EventType | EventStatus;

const ListView: React.FC<ListViewProps> = ({ events }) => {
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');

  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events;

    // Apply filter
    if (filterBy !== 'all') {
      if (['booked', 'not-booked', 'planning'].includes(filterBy)) {
        filtered = events.filter(event => event.status === filterBy);
      } else {
        filtered = events.filter(event => event.type === filterBy);
      }
    }

    // Apply sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return a.date.getTime() - b.date.getTime();
        case 'type':
          return a.type.localeCompare(b.type);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
  }, [events, sortBy, filterBy]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleEdit = (event: Event) => {
    console.log('Edit event:', event);
    // TODO: Open edit modal
  };

  const handleDelete = (event: Event) => {
    console.log('Delete event:', event);
    // TODO: Implement delete with confirmation
  };

  return (
    <ListContainer>
      <ListHeader>
        <h3 style={{ margin: 0, color: '#e5e7eb' }}>Events ({filteredAndSortedEvents.length})</h3>
        <FilterControls>
          <FilterButton
            active={filterBy === 'all'}
            onClick={() => setFilterBy('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={filterBy === 'booked'}
            onClick={() => setFilterBy('booked')}
          >
            Booked
          </FilterButton>
          <FilterButton
            active={filterBy === 'planning'}
            onClick={() => setFilterBy('planning')}
          >
            Planning
          </FilterButton>
          <FilterButton
            active={filterBy === 'not-booked'}
            onClick={() => setFilterBy('not-booked')}
          >
            Not Booked
          </FilterButton>
          <SortSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
          >
            <option value="date">Sort by Date</option>
            <option value="type">Sort by Type</option>
            <option value="status">Sort by Status</option>
            <option value="name">Sort by Name</option>
          </SortSelect>
        </FilterControls>
      </ListHeader>

      <EventList>
        {filteredAndSortedEvents.length === 0 ? (
          <EmptyState>
            <div>No events found</div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Try adjusting your filters or add a new event
            </div>
          </EmptyState>
        ) : (
          filteredAndSortedEvents.map((event) => (
            <EventItem key={event.id}>
              <EventIcon>
                {EVENT_TYPES[event.type].emoji}
              </EventIcon>
              <EventContent>
                <EventName>{event.name}</EventName>
                <EventDetails>
                  <EventDetail>
                    {formatDate(event.date)} at {formatTime(event.date)}
                  </EventDetail>
                  {event.location && (
                    <EventDetail>
                      <MapPin size={14} />
                      {event.location}
                    </EventDetail>
                  )}
                  {event.notes && (
                    <EventDetail>
                      <FileText size={14} />
                      {event.notes.length > 50 
                        ? `${event.notes.substring(0, 50)}...` 
                        : event.notes
                      }
                    </EventDetail>
                  )}
                </EventDetails>
              </EventContent>
              <StatusBadge status={event.status}>
                {STATUS_CONFIG[event.status].label}
              </StatusBadge>
              <EventActions>
                <ActionButton onClick={() => handleEdit(event)}>
                  <Edit size={16} />
                </ActionButton>
                <ActionButton 
                  className="delete" 
                  onClick={() => handleDelete(event)}
                >
                  <Trash2 size={16} />
                </ActionButton>
              </EventActions>
            </EventItem>
          ))
        )}
      </EventList>
    </ListContainer>
  );
};

export default ListView;

